import { Grid, Select, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import {
  createNumberFormatField,
  createPercentageField,
  createSelectField,
  createTextField,
} from '@roc/client-portal-shared/components';
import { SelectField, createAutocompleteField, createSliderInputField, trueFalseOptions } from '@roc/ui';
import { observer } from 'mobx-react';
import React from 'react';
import { BridgePricerStore } from '../stores/bridgePricerStore';
import { borrowerRateOptions, loanSubtypeOptions, stateOptions, borrowerPointOptions, allLendersOptions, loanTermOptions, propertyTypeOptionsFnFGU, LoaSubtypeOptions } from '../utils/bridgePricerConstants';
import {
  DateField,
  createSliderField,
} from '@roc/ui';
import { isNotBlank, YES, NO, formatDate, loanPurpose } from '@roc/feature-utils';
import axios from 'axios';
import { store } from 'apps/roc-internal-portal/src/app/stores';
import { BridgePricerService } from '../services/bridgePricerService';
import { makeStyles } from '@material-ui/core/styles';

interface PricerFieldProps {
  store: BridgePricerStore;
  helperText: string;
}

const useStyles = makeStyles(theme => ({
  slider: {
    marginTop: '2.5em',
    marginLeft: '2em',
    width: '85%',
  }
}));

const onChangedDate = async (store) => {
  try {
    const response = await store.getRateFloor(
      {
        "submissionDate": store.form.fields["submissionDate"].value,
        "lenderName": store.form.fields['lenderName'].value?.label || '',
        "propertyState": store.form.fields["propertyState"].value,
        "loanSubtype": store.form.fields["loanSubtype"].value
      });
    store.onFieldChange("floorRate", response.data);
    store.filterBorowerRateOptions(borrowerRateOptions, response.data);
    store.getBorrowerPointsOptions(
      store.form.fields["lenderId"].value,
      store.form.fields["loanTermsMonths"].value,
      formatDate(store.form.fields["submissionDate"].value, 'MM/dd/yyyy')
    );
  } catch (err) {

  }
}

const validateDate = (store) => {
  const date = store.form.fields["submissionDate"].value;
  return date && date.getFullYear();
}

const onLenderNameChanged = async (store) => {
  const actualDate = new Date();
  const currentDate = store.form.fields["submissionDate"].value;
  !currentDate && store.onFieldChange('submissionDate', actualDate);
  console.log(store.form.fields["submissionDate"].value);
  onChangedDate(store);
}

const field = FieldComponent => {
  const WrappedFieldComponent = observer(FieldComponent);
  return props => (
    <Grid item xs={props.xs ?? 12} md={props.md ?? 6}>
      <WrappedFieldComponent {...props} />
    </Grid>
  );
};

export const PropertyState = field(({ store }: PricerFieldProps) => {
  return createCustomSelectField({
    store,
    label: 'Property State',
    testId: 'propertyState',
    fieldName: 'propertyState',
    options: stateOptions,
    isRequired: !store.form.fields.loanSubtype || store.form.fields.loanSubtype !== LoaSubtypeOptions.FIX_AND_FLIP_1_4,
  });
});

export const LoanSubType = field(({ store }: PricerFieldProps) => {
  return createCustomSelectField({
    store,
    label: 'Loan Sub Type',
    testId: 'loanSubtype',
    fieldName: 'loanSubtype',
    options: loanSubtypeOptions
  })
});

export const LoanTerm = field(({ store }: PricerFieldProps) => {
  return createCustomSelectField({
    store,
    label: 'Loan Term',
    fieldName: 'loanTermsMonths',
    testId: 'loanTermsMonths',
    options: loanTermOptions
  });
});

export const LoanPurpose = field(({ store }: PricerFieldProps) => {
  return createCustomSelectField({
    store,
    label: 'Loan Purpose',
    fieldName: 'loanPurpose',
    testId: 'loanPurpose',
    options: loanPurpose,
  });
});

export const PurchaseDate = field(({ store }: PricerFieldProps) => {
  return createDateField({
    store,
    label: 'Purchase Date',
    fieldName: 'purchaseDate',
    testId: 'purchaseDate',
  })
});

export const PropertyType = field(({ store }: PricerFieldProps) => {
  return createCustomSelectField({
    store,
    label: 'Property Type',
    fieldName: 'propertyType',
    testId: 'propertyType',
    options: propertyTypeOptionsFnFGU,
  });
});

export const SubmissionDate = field(({ store }: PricerFieldProps) => {
  return createDateField({
    store,
    label: 'Submission Date',
    testId: 'submissionDate',
    fieldName: 'submissionDate',
  })
});

export const FloorRate = field(({ store }: PricerFieldProps) => {
  return createPercentageField({
    store,
    label: 'Floor Rate',
    testId: 'floorRate',
    fieldName: 'floorRate',
    disabled: true,
  })
});

export const BorrowerRate = field(({ store }: PricerFieldProps) => {
  const classes = useStyles();

  const options = store.borrowerRateOptions ? store.borrowerRateOptions : borrowerRateOptions;
  let min = 0;
  let max = 0;

  if (options.length > 0) {
    min = Number(options[0].value);
    max = Number(options[options.length - 1].value);
  }

  return createSliderInputField({
    store,
    label: 'Borrower Rate',
    fieldName: 'borrowerRate',
    testId: 'borrowerRate',
    className: classes.slider,
    valueLabelDisplay: 'on',
    min,
    max,
    step: 0.01,
    marks: [
      {
        value: min,
        label: min + '%',
      },
      {
        value: max,
        label: max + '%',
      },
    ]
  });
});

export const BorrowerPoints = field(({ store }: PricerFieldProps) => {
  const classes = useStyles();
  const options = store.borrowerPointsOptions ? store.borrowerPointsOptions : borrowerPointOptions;
  let min = 0;
  let max = 0;

  if (options.length > 0) {
    min = Number(options[0].value);
    max = Number(options[options.length - 1].value);
  }

  return createSliderInputField({
    store,
    label: "Borrower Points",
    fieldName: 'borrowerPoints',
    testId: 'borrowerPoints',
    className: classes.slider,
    valueLabelDisplay: 'on',
    min,
    max,
    step: 0.25,
    marks: min > 0 ? [
      {
        value: min,
        label: min + '%',
      },
      {
        value: max,
        label: max + '%',
      },
    ] : false,
  })
});

export const RocRate = field(({ store }: PricerFieldProps) => {
  return createPercentageField({
    store,
    label: 'Capital Provider Rate',
    testId: 'rocRate',
    fieldName: 'rocRate',
    disabled: true,
  })
});

export const RocPoints = field(({ store }: PricerFieldProps) => {
  return createPercentageField({
    store,
    label: 'Capital Provider Points',
    testId: 'rocPoints',
    fieldName: 'rocPoints',
    disabled: true,
  })
});

export const YieldSpread = field(({ store }: PricerFieldProps) => {
  return createPercentageField({
    store,
    label: 'Your Yield Spread',
    testId: 'yourYieldSpread',
    fieldName: 'yourYieldSpread',
    disabled: true,
  })
});

export const YourPoints = field(({ store }: PricerFieldProps) => {
  return createPercentageField({
    store,
    label: 'Your Points',
    testId: 'yourPoints',
    fieldName: 'yourPoints',
    disabled: true,
  })
});

export const AllLenders = field(({ store }: PricerFieldProps) => {
  const allLender: lenderType[] = store.allLenderOptions;
  const lenderName = {
    label: store.form.fields['lenderName'].value
  };

  const handleLenderAutocompleteChange = (value) => {
    store.onFieldChange('lenderName', value);
    store.onFieldChange('lenderId', value.value);
    console.log(value.value);
    onLenderNameChanged(store);
  }

  return (
    <Grid item xs={12}>
      {createAutocompleteField({
        store,
        label: 'Lender',
        fieldName: 'lenderName',
        options: allLender,
        onChange: handleLenderAutocompleteChange,
      })}
    </Grid>
  );
});

interface SelectFieldProps {
  options?: Array<{
    label: string;
    value: any;
  }>;
  variant?: 'outlined' | 'standard';
  type?: 'text' | 'number';
  label: string;
  subLabel?: string;
  fieldName: string;
  testId: string;
  store: any;
  disabled?: boolean;
  tooltip?: string;
  placeholder?: string;
  isRequired?: boolean;
}

interface lenderType {
  label: string;
  value: string;
};

interface DateFieldProps {
  store: any;
  testId: string;
  fieldName: string;
  label: string;
  disabled?: boolean;
  format?: string;
  standaloneLabel?: boolean;
  tooltip?: string;
  disableFuture?: boolean;
}

export const createDateField = ({
  testId,
  disabled,
  fieldName,
  format = 'MM/dd/yyyy',
  label,
  store,
  standaloneLabel = true,
  disableFuture,
  tooltip
}: DateFieldProps) => {
  const formFields = store.form.fields;
  const field = formFields[fieldName];
  const required = field.rule.indexOf('required') > -1;
  return (
    <DateField
      testId={testId}
      disabled={disabled}
      inputVariant="outlined"
      required={required}
      format={format}
      label={label}
      value={field.value}
      onChange={date => {
        store.onFieldChange(fieldName, date);
        validateDate(store) && onChangedDate(store)
      }}
      disableFuture={disableFuture}
      error={isNotBlank(field.error)}
      helperText={field.error}
      standaloneLabel={standaloneLabel}
      // tooltip={tooltip}
      fullWidth
    />
  );
};

export const createCustomSelectField = ({
  options,
  variant = 'outlined',
  label,
  fieldName,
  testId,
  store,
  disabled,
  tooltip,
  placeholder,
  isRequired,
}: SelectFieldProps) => {
  const formFields = store.form.fields;
  const field = formFields[fieldName];
  const required = isRequired === null ? field.rule.indexOf('required') > -1 : isRequired;

  return (
    <SelectField
      disabled={disabled}
      standaloneLabel
      label={label}
      value={field.value}
      required={required}
      options={options}
      onChange={value => {
        store.onFieldChange(fieldName, value)
        store.form.fields["lenderName"].value && onChangedDate(store);
      }}
      error={isNotBlank(field.error)}
      errorText={field.error}
      variant={variant}
      fullWidth
      testId={testId}
      tooltip={tooltip}
      placeholder={placeholder}
    />
  );
};